import api from 'services/api';
import { array, enforce, nullable, num, object, runSchema, str, withDefault, type FieldParser } from 'validation';

import { retentionPolicySchema, type RetentionPolicy } from 'api/shared/retentionPolicy';

/**
 * The FE-authored request body for creating/updating a zone. **Permissive on
 * purpose** — the server owns the rules (name required, retention shape) and
 * returns 400. One shape for both writes: `postZone` sends it as-is; `putZone`
 * maps `name` → the wire's `new_name` (the URL carries the original name), so
 * callers think in terms of "the zone's name" either way.
 */
export interface SaveZone {
  readonly name: string | null;
  readonly description: string | null;
  readonly retention_policies: readonly RetentionPolicy[];
}

// The full zone record returned by both POST and PUT — the backend serializes
// both through the SAME `getZoneDetails(client, name)` helper (routes/zones.js),
// so this is shared by IDENTITY (one query, one shape). Declared here (the
// create endpoint); `putZone` imports it + `zoneSchema`.
export interface Zone {
  readonly zone_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly retention_policies: readonly RetentionPolicy[];
}

export const zoneSchema: FieldParser<Zone> = object<Zone>({
  zone_id: num(),
  name: str(),
  description: nullable(str()),
  created_at: str(),
  updated_at: str(),
  retention_policies: withDefault(array(retentionPolicySchema), []),
});

/**
 * POST `/zones`. Returns the created zone. Throws on HTTP error (caller handles
 * the 400 path) or on structural validation failure of the response.
 */
export const postZone = async (save: SaveZone): Promise<Zone> => {
  const res = await api.post('/zones', {
    name: save.name,
    description: save.description,
    retention_policies: save.retention_policies,
  });
  const r = runSchema(zoneSchema, res.data, 'postZone');
  if (!r.ok) {
    throw new Error('postZone: response failed validation');
  }
  return enforce(r.value, r.warnings, ['zone_id']);
};
